/* eslint-disable spire/export-styles */
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState, isCartEmpty } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button from "@insite/mobius/Button";
import ArrowLeft from "@insite/mobius/Icons/ArrowLeft";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import injectCss from "@insite/mobius/utilities/injectCss";
import * as React from "react";
import { FC } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";
import isMobile from "ismobilejs";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import translate from "@insite/client-framework/Translate";
import ADIXtenExportToXtenButton from "@NA/Components/ADIXtenExportToXtenButton";
import { getPLPUrl } from "@NA/Utils/Utility";

const enum fields {
    variant = "variant",
    label = "label",
    link = "link",
    alignment = "alignment",
}

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    return {
        isCartEmpty: isCartEmpty(cart),
    };
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.variant]: "primary" | "secondary" | "tertiary";
        [fields.label]: string;
        [fields.link]: LinkFieldValue;
        [fields.alignment]: string;
    };
}

export interface ButtonStyles {
    wrapperStyles: InjectableCss;
}

const wrapperStyles: InjectableCss = {
    css: css`
        button {            
            display: flex;
            align-items: center;
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            font-weight: bold;
            svg {
                width: 13px;
                margin-right: 6px;
                stroke: ${getColor("text.main")};
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        }
    `,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export const ADIContinueShoppingButton: FC<Props> = ({ fields, isCartEmpty }) => {
  

    const history = useHistory();
    const link = useGetLink(fields.link);
    const { isInShell } = useShellContext();
    const styles = useMergeStyles("button", { wrapperStyles, });

    if (isCartEmpty) {
        return null;
    }

    const onClick = () => {
        const plpUrl = getPLPUrl();
        if (!link.url) {
            return;
        }

        if (link.url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = link.url;
        } else {
            if (plpUrl != null && plpUrl != "" && plpUrl != undefined) {
                history.push(plpUrl as string);
            } else {
                history.push("/");
            }
        }
    };

 

    return (
        <ContentWrapper alignment={fields.alignment} {...styles?.wrapperStyles}>
            <Button variant={fields.variant} onClick={onClick} style={{textTransform: 'capitalize', minWidth: '195px'}}>
                <ArrowLeft />
                {fields.label}
            </Button>

            <ADIXtenExportToXtenButton                             
                                id="current"
                                type="cart"
                                buttonText=""
                                />
        </ContentWrapper>
    );
};

export const ContentWrapper = styled.div<InjectableCss & { alignment: string }>`
    display: flex;
    width: 100%;
    justify-content: ${props => props.alignment ?? "flex-start"};
    ${injectCss};
`;

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(ADIContinueShoppingButton),
    definition: {
        group: "Basic",
        icon: "Button",
        fieldDefinitions: [
            {
                name: fields.variant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                fieldType: "General",
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.link,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
            },
            {
                name: fields.alignment,
                displayName: "Button Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "primary",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
